import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Search, X } from "lucide-react";
import { CATEGORY_ICON_FALLBACK, Icon } from "@/ds";
import api from "@/lib/api";
import { categoryLabel } from "@/lib/catalog";

/**
 * Pick the trades a FixyBee works in: big tiles, one tap each (2026-10-28).
 *
 * Replaces the onboarding step that led with an AI text box and hid the list
 * behind "or pick from the full list". A handyman on a phone recognises a
 * picture of a wrench faster than he describes his trade in a sentence, so the
 * list IS the screen now: a tile per category with its glyph and its name, a
 * check on the chosen ones, the chosen ones repeated as chips on top (they
 * scroll out of sight on a long grid), and a search box for people who would
 * rather type "plom".
 *
 * **One source.** Categories come from `GET /catalog/categories`, the same list
 * the home grid and the directory use; glyphs from each row's own `icon`;
 * names from i18n through `categoryLabel` — never from the slug, and never
 * from the English name when a translation exists.
 *
 * `max={1}` is sign-up (one main trade, replaced by the next tap); no `max` is
 * Settings, where they add the rest.
 */
export default function CategoryPicker({ value, onChange, max = null, testid = "category-picker" }) {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let alive = true;
    api.get("/catalog/categories")
      .then((r) => { if (alive) setCategories(r.data.categories || []); })
      .catch(() => { if (alive) setCategories([]); });
    return () => { alive = false; };
  }, []);

  const labelled = useMemo(
    () => (categories || []).map((c) => ({ ...c, label: categoryLabel(t, c) })),
    // i18n.language: the labels are read in the current language.
    [categories, t, i18n.language], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const shown = useMemo(() => {
    const q = fold(query);
    if (!q) return labelled;
    // Both languages: a Spanish speaker on an English page still types "plomero".
    return labelled.filter((c) =>
      [c.label, c.name, c.name_es].some((s) => fold(s).includes(q)));
  }, [labelled, query]);

  const toggle = (id) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else if (max === 1) {
      onChange([id]);
    } else if (!max || value.length < max) {
      onChange([...value, id]);
    }
  };

  const picked = labelled.filter((c) => value.includes(c.category_id));

  if (categories === null) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`${testid}-loading`}>
        {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-24 rounded-md" />)}
      </div>
    );
  }

  return (
    <div data-testid={testid}>
      {picked.length > 0 && (
        <div className="mb-4" data-testid={`${testid}-picked`}>
          <p className="text-sm text-smoke mb-2">{t("signupX.categoryPicked")}</p>
          <div className="flex flex-wrap gap-2">
            {picked.map((c) => (
              <button
                key={c.category_id}
                type="button"
                onClick={() => toggle(c.category_id)}
                aria-label={t("signupX.categoryRemove", { name: c.label })}
                className="fx-chip inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full border border-hive bg-hive/20 text-worker font-display font-semibold text-sm"
                data-testid={`${testid}-chip-${c.slug}`}
              >
                {c.label} <X size={16} aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-smoke" aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("signupX.categorySearch")}
          aria-label={t("signupX.categorySearch")}
          className="input-base pl-10 min-h-[48px]"
          data-testid={`${testid}-search`}
        />
      </div>

      {shown.length === 0 ? (
        <p className="text-smoke text-sm py-6 text-center" data-testid={`${testid}-empty`}>
          {t("signupX.categoryNone")}
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" role="group">
          {shown.map((c) => {
            const on = value.includes(c.category_id);
            return (
              <button
                key={c.category_id}
                type="button"
                onClick={() => toggle(c.category_id)}
                aria-pressed={on}
                className={`fx-chip relative flex flex-col items-center justify-center gap-2 min-h-[96px] p-3 rounded-md border-2 text-center transition-colors ${
                  on ? "border-hive-deep bg-hive/20" : "border-sand bg-cream hover:border-worker"
                }`}
                data-testid={`${testid}-tile-${c.slug}`}
              >
                {on && (
                  <span className="absolute top-2 right-2 h-6 w-6 rounded-full bg-worker text-cream inline-flex items-center justify-center" aria-hidden="true">
                    <Check size={14} strokeWidth={3} />
                  </span>
                )}
                <Icon name={c.icon || CATEGORY_ICON_FALLBACK} size={30}
                      style={{ color: on ? "var(--hive-ink)" : "var(--text-muted)" }} />
                <span className="font-display text-sm font-semibold text-worker leading-tight">{c.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Lowercase without accents, so "plomeria" finds "Plomería". */
function fold(s) {
  return String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}
